/**
 * Shared state for invoicing screens: the selected shop (from the dashboard
 * store, like every other module) and the shop's invoice settings, fetched
 * once per shop and shared across screens for the session.
 */
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import InvoiceRepository, { InvoiceSettings } from '../../internals/repository/InvoiceRepository';

export function useSelectedShop(): any {
  return useSelector((state: any) => state.dashboard.get('selectedShop'));
}

const cache = new Map<string, Promise<InvoiceSettings>>();

export function invalidateSettings(shopId?: string) {
  if (shopId) cache.delete(shopId);
  else cache.clear();
}

export function useInvoiceSettings(shopId?: string) {
  const [settings, setSettings] = useState<InvoiceSettings | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(() => {
    if (!shopId) return;
    if (!cache.has(shopId)) {
      cache.set(shopId, InvoiceRepository.getSettings(shopId).then((r) => r.settings));
    }
    cache.get(shopId)!
      .then((s) => { setSettings(s); setError(null); })
      .catch((e) => { cache.delete(shopId); setError(e); });
  }, [shopId]);

  useEffect(() => { setSettings(null); load(); }, [load]);

  const replace = (next: InvoiceSettings) => {
    if (shopId) cache.set(shopId, Promise.resolve(next));
    setSettings(next);
  };

  return { settings, error, reload: () => { if (shopId) cache.delete(shopId); load(); }, replace };
}

/** The default tax rate, used to pre-fill new lines. */
export const defaultTaxCode = (settings?: InvoiceSettings | null) =>
  settings?.taxRates?.find((r) => r.isDefault)?.code ?? settings?.taxRates?.[0]?.code;
